import { Dialog } from '@headlessui/react';
import React, { useEffect, useRef, useState } from 'react';

export type ImageContentBlock = {
  type: 'image';
  src: string;
  alt: string;
  caption?: string;
  display?: 'contain' | 'cover';
  allow_zoom?: boolean;
};

export type VideoContentBlock = {
  type: 'video';
  src: string;
  poster?: string;
  caption?: string;
  transcript?: string;
  allow_speed_control?: boolean;
};

const isSafeHttpsUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && Boolean(parsed.hostname) && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
};

const MediaError: React.FC<{ caption?: string; src?: string }> = ({ caption, src }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="status">
    <p className="font-medium">This media could not be loaded.</p>
    {caption ? <p className="mt-1">{caption}</p> : null}
    {isSafeHttpsUrl(src) ? (
      <a className="mt-2 inline-flex font-medium text-blue-700 underline" href={src} target="_blank" rel="noreferrer">
        Open media in a new tab
      </a>
    ) : null}
  </div>
);

export const ImageBlock: React.FC<{ block: ImageContentBlock }> = ({ block }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);
  const safe = isSafeHttpsUrl(block.src);
  const allowZoom = block.allow_zoom !== false;

  useEffect(() => {
    setLoading(true);
    setFailed(false);
    setOpen(false);
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [block.src]);

  const reset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };
  const close = () => {
    setOpen(false);
    reset();
  };
  const changeScale = (nextScale: number) => {
    const bounded = Math.min(4, Math.max(1, nextScale));
    setScale(bounded);
    if (bounded === 1) setOffset({ x: 0, y: 0 });
  };

  if (!safe || failed) return <MediaError caption={block.caption} src={block.src} />;

  const image = (
    <img
      src={block.src}
      alt={block.alt}
      className={`max-h-[70vh] w-full rounded-lg ${block.display === 'cover' ? 'object-cover' : 'object-contain'}`}
      onLoad={() => setLoading(false)}
      onError={() => {
        setLoading(false);
        setFailed(true);
      }}
    />
  );

  return (
    <figure className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      {loading ? <div className="flex min-h-40 items-center justify-center text-sm text-slate-500" role="status">Loading image…</div> : null}
      <div className={loading ? 'hidden' : ''}>
        {allowZoom ? (
          <button
            type="button"
            className="block w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            onClick={() => setOpen(true)}
            aria-label={`Enlarge image: ${block.alt}`}
          >
            {image}
          </button>
        ) : image}
      </div>
      {block.caption ? <figcaption className="mt-3 text-sm text-slate-600">{block.caption}</figcaption> : null}

      <Dialog open={open} onClose={close} className="relative z-[80]">
        <div className="fixed inset-0 bg-slate-950/90" aria-hidden="true" />
        <div className="fixed inset-0 flex flex-col p-3 sm:p-6">
          <Dialog.Panel className="flex min-h-0 flex-1 flex-col rounded-xl bg-slate-950 text-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/20 p-3">
              <Dialog.Title className="sr-only">Enlarged image</Dialog.Title>
              <div className="flex gap-2">
                <button type="button" aria-label="Zoom out" className="rounded border border-white/40 px-3 py-2 focus-visible:ring-2 focus-visible:ring-white" onClick={() => changeScale(scale - 0.25)}>−</button>
                <button type="button" aria-label="Zoom in" className="rounded border border-white/40 px-3 py-2 focus-visible:ring-2 focus-visible:ring-white" onClick={() => changeScale(scale + 0.25)}>+</button>
                <button type="button" aria-label="Reset zoom" className="rounded border border-white/40 px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-white" onClick={reset}>Reset</button>
              </div>
              <button type="button" aria-label="Close enlarged image" className="rounded border border-white/40 px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-white" onClick={close}>Close</button>
            </div>
            <div
              className={`relative min-h-0 flex-1 overflow-hidden ${scale > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
              tabIndex={0}
              aria-label="Zoomed image canvas. Use arrow keys to pan."
              onKeyDown={(event) => {
                if (scale <= 1) return;
                const amount = 24;
                if (event.key === 'ArrowLeft') setOffset((prev) => ({ ...prev, x: prev.x + amount }));
                else if (event.key === 'ArrowRight') setOffset((prev) => ({ ...prev, x: prev.x - amount }));
                else if (event.key === 'ArrowUp') setOffset((prev) => ({ ...prev, y: prev.y + amount }));
                else if (event.key === 'ArrowDown') setOffset((prev) => ({ ...prev, y: prev.y - amount }));
                else return;
                event.preventDefault();
              }}
              onPointerDown={(event) => {
                if (scale <= 1) return;
                event.currentTarget.setPointerCapture(event.pointerId);
                dragRef.current = { x: event.clientX, y: event.clientY, originX: offset.x, originY: offset.y };
              }}
              onPointerMove={(event) => {
                if (!dragRef.current) return;
                setOffset({
                  x: dragRef.current.originX + event.clientX - dragRef.current.x,
                  y: dragRef.current.originY + event.clientY - dragRef.current.y,
                });
              }}
              onPointerUp={() => { dragRef.current = null; }}
            >
              <img
                src={block.src}
                alt={block.alt}
                draggable={false}
                className="h-full w-full select-none object-contain transition-transform duration-100"
                style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
              />
            </div>
            {block.caption ? <p className="border-t border-white/20 p-3 text-sm text-slate-200">{block.caption}</p> : null}
          </Dialog.Panel>
        </div>
      </Dialog>
    </figure>
  );
};

export const VideoBlock: React.FC<{
  block: VideoContentBlock;
  sessionKey?: string;
  preservePosition?: boolean;
}> = ({ block, sessionKey, preservePosition = false }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const safe = isSafeHttpsUrl(block.src);
  const storageKey = sessionKey ? `elevate:media-position:${sessionKey}` : '';

  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (!video) return;
      video.pause();
      if (document.fullscreenElement === video && document.exitFullscreen) {
        void Promise.resolve(document.exitFullscreen()).catch(() => undefined);
      }
    };
  }, [block.src, sessionKey]);

  if (!safe || failed) return <MediaError caption={block.caption} src={block.src} />;

  return (
    <figure className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      {loading ? <div className="flex min-h-40 items-center justify-center text-sm text-slate-500" role="status">Loading video…</div> : null}
      <video
        ref={videoRef}
        className={`max-h-[70vh] w-full rounded-lg bg-black ${loading ? 'hidden' : ''}`}
        src={block.src}
        poster={isSafeHttpsUrl(block.poster) ? block.poster : undefined}
        controls
        preload="metadata"
        playsInline
        onLoadedMetadata={(event) => {
          setLoading(false);
          if (!preservePosition || !storageKey) return;
          const saved = Number(window.sessionStorage.getItem(storageKey));
          if (Number.isFinite(saved) && saved > 0 && saved < event.currentTarget.duration) {
            event.currentTarget.currentTime = saved;
          }
        }}
        onTimeUpdate={(event) => {
          if (preservePosition && storageKey) {
            window.sessionStorage.setItem(storageKey, String(event.currentTarget.currentTime));
          }
        }}
        onError={() => {
          setLoading(false);
          setFailed(true);
        }}
      />
      {block.allow_speed_control ? (
        <label className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700">
          Playback speed
          <select
            className="rounded border border-slate-300 bg-white px-2 py-1 focus-visible:ring-2 focus-visible:ring-blue-600"
            defaultValue="1"
            onChange={(event) => {
              if (videoRef.current) videoRef.current.playbackRate = Number(event.target.value);
            }}
          >
            <option value="0.5">0.5×</option>
            <option value="0.75">0.75×</option>
            <option value="1">1×</option>
            <option value="1.25">1.25×</option>
            <option value="1.5">1.5×</option>
            <option value="2">2×</option>
          </select>
        </label>
      ) : null}
      {block.caption ? <figcaption className="mt-3 text-sm text-slate-600">{block.caption}</figcaption> : null}
      {isSafeHttpsUrl(block.transcript) ? (
        <a className="mt-2 inline-flex font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600" href={block.transcript} target="_blank" rel="noreferrer">
          Open transcript
        </a>
      ) : null}
    </figure>
  );
};

export { isSafeHttpsUrl };
